import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, LogOut, Clock, Maximize2, Minimize2, ShieldAlert, Sparkles, Hand, LayoutGrid, Square, FileText, Settings as SettingsIcon, Lock, Video as VideoIcon, StopCircle, GraduationCap, User } from 'lucide-react';
import { toast } from 'sonner';
import { classroomApi } from '@/lib/classroomApi';
import VideoPanel from '@/components/classroom/VideoPanel';
import DailyVideoPanel, { getActiveCallObject } from '@/components/classroom/DailyVideoPanel';
import ChatPanel from '@/components/classroom/ChatPanel';
import MultiBoardPanel from '@/components/classroom/MultiBoardPanel';
import DocumentsPanel from '@/components/classroom/DocumentsPanel';
import ResourcesPanel from '@/components/classroom/ResourcesPanel';
import LessonNotesPanel from '@/components/classroom/LessonNotesPanel';
import CursorOverlay from '@/components/classroom/CursorOverlay';
import MathNotesPanel from '@/components/classroom/MathNotesPanel';
import ClassroomSettingsPanel from '@/components/classroom/ClassroomSettingsPanel';
import RecapPanel from '@/components/classroom/RecapPanel';
import DeviceCheckPanel from '@/components/classroom/DeviceCheckPanel';
import Seo from '@/components/shared/Seo';

// ---------------- Phase 9A helpers ----------------
const PREPARE_BEFORE_MS = 10 * 60 * 1000;     // 10 minutes early
const GRACE_AFTER_MS    = 15 * 60 * 1000;     // 15 minutes grace

const computePhase = (startISO, endISO, status) => {
  if (status === 'completed') return 'finished';
  if (!startISO || !endISO) return 'scheduled';
  const now = Date.now();
  const s = new Date(startISO).getTime();
  const e = new Date(endISO).getTime();
  if (isNaN(s) || isNaN(e)) return 'scheduled';
  if (now < s - PREPARE_BEFORE_MS) return 'scheduled';
  if (now < s) return 'preparing';
  if (now < e) return 'live';
  if (now < e + GRACE_AFTER_MS) return 'live';   // grace period still counts as live-ish
  return 'finished';
};

const fmtCountdown = (ms) => {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const useElapsedSeconds = (startedAtMs) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!startedAtMs) return;
    const id = setInterval(() => setN(Math.floor((Date.now() - startedAtMs) / 1000)), 1000);
    return () => clearInterval(id);
  }, [startedAtMs]);
  return n;
};

const fmtElapsed = (s) => `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const ClassroomPage = () => {
  const { roomId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') || '';

  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [joinedAt, setJoinedAt] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [layout, setLayout] = useState('split');   // 'split' | 'whiteboard' | 'document'
  const [callObject, setCallObject] = useState(null);
  const [handRaisedBy, setHandRaisedBy] = useState(null);  // counterpart name when they raise
  const [settings, setSettings] = useState({
    default_layout: 'split',
    cursors_enabled: true,
    math_enabled: true,
    allow_student_draw_on_pdf: false,
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [ended, setEnded] = useState(false);
  const [summary, setSummary] = useState(null);

  // Global 1-second tick for countdown + remaining time.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Poll for the Daily callObject becoming available after the user clicks Join.
  useEffect(() => {
    if (!joined) return;
    let n = 0;
    const id = setInterval(() => {
      const co = getActiveCallObject();
      if (co) { setCallObject(co); clearInterval(id); }
      if (++n > 40) clearInterval(id);  // give up after ~10s
    }, 250);
    return () => clearInterval(id);
  }, [joined]);

  // Listen for hand-raise + layout broadcasts
  useEffect(() => {
    if (!callObject) return;
    const handler = (e) => {
      const m = e?.data; if (!m) return;
      if (m.kind === 'wh-hand') {
        if (m.raised) {
          setHandRaisedBy(m.from || 'A participant');
          toast.info(`✋ ${m.from || 'A participant'} raised their hand`);
        } else {
          setHandRaisedBy(null);
        }
      }
      if (m.kind === 'wh-layout' && ['split', 'whiteboard', 'document'].includes(m.layout)) {
        setLayout(m.layout);
      }
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject]);

  const raiseHand = () => {
    try { callObject?.sendAppMessage({ kind: 'wh-hand', raised: true, from: ctx?.your_name }); } catch (_e) {}
    toast.success('Hand raised');
    setTimeout(() => {
      try { callObject?.sendAppMessage({ kind: 'wh-hand', raised: false }); } catch (_e) {}
    }, 5000);
  };

  const setLayoutAndBroadcast = (next) => {
    setLayout(next);
    if (ctx?.role === 'tutor') {
      try { callObject?.sendAppMessage({ kind: 'wh-layout', layout: next }); } catch (_e) {}
    }
  };

  const leave = async () => {
    try { await classroomApi.attendance(roomId, token, 'leave'); } catch (_e) {}
    window.location.assign('/');
  };

  const videoBlock = ctx && (ctx.video_provider === 'daily' && ctx.provider_room_url ? (
    ctx.provider_meeting_token ? (
      <DailyVideoPanel
        roomUrl={ctx.provider_room_url}
        token={ctx.provider_meeting_token}
        role={ctx.role}
        yourName={ctx.your_name}
        counterpartName={ctx.counterpart_name}
        onLeave={leave}
      />
    ) : (
      <div className="relative w-full min-h-[460px] rounded-2xl bg-[#0a0a0a] border border-white/10 flex flex-col items-center justify-center text-white px-6 text-center"
           data-testid="classroom-token-retry">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D3D86F] mb-2">Winhouse Classroom</div>
        <div className="text-base font-bold mb-2">Preparing your secure video link…</div>
        <div className="text-xs text-white/60 mb-4 max-w-sm">If this persists, click the button below to refresh.</div>
        <button onClick={() => window.location.reload()}
          className="h-10 px-5 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider"
          data-testid="classroom-token-retry-btn">
          Reconnect
        </button>
      </div>
    )
  ) : (
    <VideoPanel role={ctx.role} yourName={ctx.your_name} counterpartName={ctx.counterpart_name}
      provider={ctx.video_provider} providerReady={ctx.video_provider !== 'stub'}
      diagnostics={ctx.video_diagnostics} />
  ));

  // Load room context
  useEffect(() => {
    if (!roomId || !token) { setErr('Missing classroom token.'); setLoading(false); return; }
    (async () => {
      try {
        const c = await classroomApi.roomContext(roomId, token);
        setCtx(c);
      } catch (e) {
        setErr(e.message || 'Could not load classroom');
      } finally { setLoading(false); }
    })();
  }, [roomId, token]);

  // Fetch room settings once context is available; apply default_layout
  useEffect(() => {
    if (!ctx) return;
    classroomApi.settingsGet(roomId, token)
      .then((r) => {
        if (r?.settings) {
          setSettings(r.settings);
          if (['split', 'whiteboard', 'document'].includes(r.settings.default_layout)) {
            setLayout(r.settings.default_layout);
          }
        }
      })
      .catch(() => {});
  }, [ctx, roomId, token]);

  // Listen for settings updates broadcast by tutor
  useEffect(() => {
    if (!callObject) return;
    const handler = (e) => {
      const m = e?.data; if (!m) return;
      if (m.kind === 'wh-settings' && m.settings) setSettings(m.settings);
    };
    callObject.on('app-message', handler);
    return () => { try { callObject.off('app-message', handler); } catch (_e) {} };
  }, [callObject]);

  // Auto-record 'join' attendance once context loads
  const join = async () => {
    try {
      await classroomApi.attendance(roomId, token, 'join');
      setJoined(true);
      setJoinedAt(Date.now());
    } catch (_e) { /* non-blocking */ }
  };

  // Beforeunload: record leave best-effort
  useEffect(() => {
    if (!joined) return;
    const handler = () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/classroom/room/${roomId}/attendance?t=${encodeURIComponent(token)}`;
        navigator.sendBeacon?.(url, new Blob([JSON.stringify({ event: 'leave' })], { type: 'application/json' }));
      } catch (_e) {}
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [joined, roomId, token]);

  const elapsed = useElapsedSeconds(joinedAt);

  const startISO = ctx?.start_time;
  const endISO = ctx?.end_time;
  const phase = useMemo(() => computePhase(startISO, endISO, ended ? 'completed' : ctx?.status),
                          // eslint-disable-next-line react-hooks/exhaustive-deps
                          [startISO, endISO, ctx?.status, ended, nowTick]);
  const countdownToStart = useMemo(() => {
    if (!startISO) return 0;
    return new Date(startISO).getTime() - nowTick;
  }, [startISO, nowTick]);
  const remainingMs = useMemo(() => {
    if (!endISO) return 0;
    return Math.max(0, new Date(endISO).getTime() - nowTick);
  }, [endISO, nowTick]);
  const isTutorOrAdmin = ctx?.role === 'tutor' || ctx?.role === 'admin';

  const endLesson = async () => {
    if (!window.confirm('End this lesson for both parties? A summary will be generated.')) return;
    try {
      const r = await classroomApi.endLesson(roomId, token);
      setSummary(r.summary);
      setEnded(true);
      toast.success('Lesson ended — summary generated.');
      try { await classroomApi.attendance(roomId, token, 'leave'); } catch (_e) {}
    } catch (e) {
      toast.error(e.message || 'Could not end lesson');
    }
  };

  // Auto-fetch summary when phase transitions to finished (or ended manually)
  useEffect(() => {
    if (phase !== 'finished' || summary) return;
    classroomApi.lessonSummary(roomId, token)
      .then((r) => { if (r?.exists) setSummary(r.summary); })
      .catch(() => {});
  }, [phase, roomId, token, summary]);

  const classWindow = useMemo(() => {
    if (!startISO || !endISO) return '';
    try {
      return `${new Date(startISO).toLocaleString()} → ${new Date(endISO).toLocaleTimeString()}`;
    } catch (_e) { return ''; }
  }, [startISO, endISO]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]" data-testid="classroom-loading">
        <Loader2 className="h-7 w-7 animate-spin text-red-700" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4" data-testid="classroom-error">
        <Seo title="Classroom · Winhouse" canonical="/classroom" />
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="h-5 w-5 text-red-700" />
          </div>
          <h1 className="text-xl font-bold mb-2">You do not have access to this classroom.</h1>
          <p className="text-sm text-gray-600 mb-5">Check your invitation link or contact your tutor.</p>
          <a href="/" className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-black text-white text-sm font-semibold">Return home</a>
        </div>
      </div>
    );
  }

  if (ctx && ctx.access === 'awaiting_payment') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4" data-testid="classroom-awaiting-payment">
        <Seo title="Awaiting Payment · Winhouse Classroom" canonical={`/classroom/${roomId}`} />
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="h-5 w-5 text-amber-600" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-1.5">Winhouse Classroom</div>
          <h1 className="text-xl font-bold mb-2">Awaiting payment</h1>
          <p className="text-sm text-gray-600 mb-5">
            Your class for <span className="font-semibold">{ctx.subject}</span> is reserved. Once payment is confirmed by Winhouse, this room will unlock automatically.
          </p>
          <a href="mailto:info@winhousetutors.com?subject=Class payment query"
            className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-black text-white text-sm font-semibold">
            Contact Winhouse
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#FAF9F6] overflow-x-hidden ${fullscreen ? 'fixed inset-0 z-50 overflow-y-auto' : 'min-h-screen'}`} data-testid="classroom-page">
      <Seo title={`${ctx?.subject || 'Class'} · Winhouse Classroom`} canonical={`/classroom/${roomId}`} />
      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-black text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="h-4 w-4 text-[#D3D86F] flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">
                Winhouse Classroom · Lesson #{ctx.lesson_number || 1} · {ctx.program || ctx.subject}
              </div>
              <div className="text-sm font-bold truncate" data-testid="classroom-header-subject">
                {ctx.subject}
                <span className="ml-2 text-white/60 font-normal text-[11px] hidden md:inline">
                  {ctx.tutor_name} → {ctx.student_name}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {phase === 'live' && (
              <span className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600 text-white text-[10px] font-black uppercase tracking-wider animate-pulse"
                  data-testid="classroom-live-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-white" /> LIVE
              </span>
            )}
            {phase === 'preparing' && (
              <span className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider"
                  data-testid="classroom-preparing-badge">READY</span>
            )}
            <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-white/70">
              <Clock className="h-3.5 w-3.5" />
              {joined
                ? fmtElapsed(elapsed)
                : (phase === 'scheduled' ? fmtCountdown(countdownToStart)
                  : (phase === 'live' ? `${fmtCountdown(remainingMs)} left` : 'Not joined'))}
            </span>
            {handRaisedBy && (
              <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400 text-black text-[11px] font-bold uppercase tracking-wider animate-pulse"
                    data-testid="classroom-handraised">
                ✋ {handRaisedBy}
              </span>
            )}
            {joined && (
              <>
                {/* Layout switcher */}
                <div className="hidden md:flex items-center bg-white/5 rounded-full p-0.5 border border-white/10">
                  <button onClick={() => setLayoutAndBroadcast('split')}
                    className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${layout === 'split' ? 'bg-white text-black' : 'text-white/70 hover:text-white'}`}
                    data-testid="layout-split"><LayoutGrid className="h-3 w-3" /> Split</button>
                  <button onClick={() => setLayoutAndBroadcast('whiteboard')}
                    className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${layout === 'whiteboard' ? 'bg-white text-black' : 'text-white/70 hover:text-white'}`}
                    data-testid="layout-whiteboard"><Square className="h-3 w-3" /> Board</button>
                  <button onClick={() => setLayoutAndBroadcast('document')}
                    className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 ${layout === 'document' ? 'bg-white text-black' : 'text-white/70 hover:text-white'}`}
                    data-testid="layout-document"><FileText className="h-3 w-3" /> Doc</button>
                </div>
                <button onClick={raiseHand}
                  className="h-9 px-3 rounded-full bg-amber-400 hover:bg-amber-500 text-black text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
                  data-testid="classroom-handraise">
                  <Hand className="h-3.5 w-3.5" /> Raise hand
                </button>
              </>
            )}
            <button onClick={() => setFullscreen((f) => !f)}
              className="hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition"
              data-testid="classroom-fullscreen">
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button onClick={() => setSettingsOpen(true)}
              className="hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition"
              data-testid="classroom-settings-open">
              <SettingsIcon className="h-4 w-4" />
            </button>
            {isTutorOrAdmin && phase !== 'finished' && !ended && (
              <button onClick={endLesson}
                className="hidden md:inline-flex h-9 px-3 rounded-full bg-amber-500 hover:bg-amber-600 text-black text-xs font-black uppercase tracking-wider items-center gap-1.5"
                data-testid="classroom-end-lesson">
                <StopCircle className="h-3.5 w-3.5" /> End lesson
              </button>
            )}
            <button onClick={leave}
              className="h-9 px-4 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5"
              data-testid="classroom-leave">
              <LogOut className="h-3.5 w-3.5" /> Leave
            </button>
          </div>
        </div>
      </div>

      <main className="relative max-w-7xl mx-auto px-4 py-6">
        {joined && (
          <CursorOverlay
            callObject={callObject}
            role={ctx.role}
            yourName={ctx.your_name}
            counterpartName={ctx.counterpart_name}
            enabled={settings.cursors_enabled !== false}
          />
        )}
        {!joined && phase === 'finished' && (
          <div className="mb-5 bg-white border border-emerald-200 rounded-2xl p-8 text-center" data-testid="classroom-finished-panel">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
              <GraduationCap className="h-7 w-7 text-emerald-700" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">Lesson complete</div>
            <div className="text-xl font-black text-black mt-1">Thanks for a great session!</div>
            <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
              {ctx.subject} · {ctx.tutor_name} → {ctx.student_name}<br />
              {summary?.planned_minutes ? `Planned duration: ${summary.planned_minutes} min · ` : ''}
              Recording {ctx.recording?.enabled ? 'is processing' : 'was not enabled'}.
            </p>
            <div className="flex justify-center gap-2 mt-5">
              <a href={`#recap-${roomId}`} onClick={(ev) => { ev.preventDefault(); document.getElementById(`recap-${roomId}`)?.scrollIntoView({ behavior: 'smooth' }); }}
                className="h-11 px-5 rounded-full bg-black hover:bg-gray-800 text-[#D3D86F] text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5"
                data-testid="classroom-view-recap">
                <FileText className="h-3.5 w-3.5" /> View recap
              </a>
              <a href="/" data-testid="classroom-return-dashboard"
                className="h-11 px-5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5">
                Return to dashboard
              </a>
            </div>
          </div>
        )}

        {!joined && phase === 'scheduled' && (
          <div className="mb-5 bg-white border border-gray-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            data-testid="classroom-scheduled-panel">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Lock className="h-5 w-5 text-gray-700" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Classroom locked</div>
              <div className="text-lg font-bold text-black mt-0.5">Your lesson begins at {new Date(startISO).toLocaleString()}</div>
              <div className="text-xs text-gray-500 mt-1">
                {ctx.subject} · {ctx.tutor_name} → {ctx.student_name} · Lesson #{ctx.lesson_number || 1}
              </div>
              <div className="mt-3 inline-flex items-center gap-2 bg-black text-white px-3 py-1.5 rounded-full">
                <Clock className="h-3 w-3 text-[#D3D86F]" />
                <span className="font-mono text-xs font-black" data-testid="classroom-countdown">
                  {fmtCountdown(countdownToStart)}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-white/60">until unlock</span>
              </div>
            </div>
            <button disabled
              className="h-11 px-6 rounded-full bg-gray-200 text-gray-500 text-sm font-semibold cursor-not-allowed"
              data-testid="classroom-join-locked">
              Join class
            </button>
          </div>
        )}

        {!joined && (phase === 'preparing' || phase === 'live') && (
          <div className="mb-5 grid lg:grid-cols-3 gap-4" data-testid="classroom-prejoin-panel">
            <div className="lg:col-span-2 bg-white border border-emerald-200 rounded-2xl p-6">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                  {phase === 'live' ? 'Classroom is live' : 'Classroom ready'}
                </div>
              </div>
              <div className="text-lg font-bold text-black mt-1">
                {phase === 'live' ? "Your tutor is waiting — click to join." : 'You may join early to test your setup.'}
              </div>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1"><User className="h-3 w-3" /> {ctx.tutor_name}</span>
                <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1"><GraduationCap className="h-3 w-3" /> {ctx.student_name}</span>
                <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1">{ctx.program || ctx.subject}</span>
                <span className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1"><Clock className="h-3 w-3" /> {classWindow}</span>
                {phase === 'live' && (
                  <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 rounded-full px-2 py-1 font-bold">
                    {fmtCountdown(remainingMs)} remaining
                  </span>
                )}
              </div>
              <button onClick={join}
                className="mt-4 h-12 px-8 rounded-full bg-red-700 hover:bg-red-800 text-white text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2"
                data-testid="classroom-join">
                <VideoIcon className="h-4 w-4" /> Join classroom
              </button>
            </div>
            <div>
              <DeviceCheckPanel />
            </div>
          </div>
        )}

        <div className={`grid gap-4 min-w-0 ${layout === 'whiteboard' ? 'lg:grid-cols-4' : layout === 'document' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`} style={{ gridAutoFlow: 'row' }}>
          {layout === 'split' && (
            <>
              {/* Row 1: video (2/3) + chat (1/3) */}
              <div className="lg:col-span-2">
                {videoBlock}
              </div>
              <div>
                <ChatPanel roomId={roomId} token={token} role={ctx.role} />
              </div>
              {/* Row 2: whiteboard full width */}
              <div className="lg:col-span-3">
                <MultiBoardPanel roomId={roomId} token={token} role={ctx.role} callObject={callObject} />
              </div>
              {/* Row 3: documents full width */}
              <div className="lg:col-span-3">
                <DocumentsPanel roomId={roomId} token={token} role={ctx.role} callObject={callObject} />
              </div>
              {/* Row 4: notes (2/3) + resources (1/3) */}
              <div className="lg:col-span-2">
                <LessonNotesPanel roomId={roomId} token={token} role={ctx.role} />
              </div>
              <div>
                <ResourcesPanel roomId={roomId} token={token} role={ctx.role} />
              </div>
              {/* Row 5: math notes (full width, collapsible) */}
              {settings.math_enabled && (
                <div className="lg:col-span-3">
                  <MathNotesPanel roomId={roomId} token={token} role={ctx.role} callObject={callObject} enabled={settings.math_enabled} />
                </div>
              )}
              {/* Row 6: lesson recap export (Phase 3A-3) */}
              <div className="lg:col-span-3" id={`recap-${roomId}`}>
                <RecapPanel roomId={roomId} token={token} role={ctx.role} />
              </div>
            </>
          )}
          {layout === 'whiteboard' && (
            <>
              <div className="lg:col-span-3">
                <MultiBoardPanel roomId={roomId} token={token} role={ctx.role} callObject={callObject} />
              </div>
              <div className="space-y-4">
                {videoBlock}
                <ChatPanel roomId={roomId} token={token} role={ctx.role} />
              </div>
            </>
          )}
          {layout === 'document' && (
            <>
              <div className="lg:col-span-3">
                <DocumentsPanel roomId={roomId} token={token} role={ctx.role} callObject={callObject} />
              </div>
              <div className="space-y-4">
                {videoBlock}
                <ChatPanel roomId={roomId} token={token} role={ctx.role} />
              </div>
            </>
          )}
        </div>
      </main>

      <ClassroomSettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        roomId={roomId}
        token={token}
        role={ctx.role}
        settings={settings}
        onChange={(next) => {
          setSettings(next);
          // Broadcast new settings to the counterpart
          try { callObject?.sendAppMessage({ kind: 'wh-settings', settings: next }); } catch (_e) {}
          // Apply default layout immediately
          if (['split', 'whiteboard', 'document'].includes(next.default_layout)) {
            setLayoutAndBroadcast(next.default_layout);
          }
        }}
      />
    </div>
  );
};

export default ClassroomPage;
